/**
 * The list view's rules: which filters it offers, how it orders and groups the
 * findings, and the one-word status each row shows. Pure, so the rules are
 * pinned by tests rather than buried in JSX. Ported from the legacy console's
 * `Output.tsx`.
 */

import type { FindingId } from "../../data/review-data"
import {
  isCorrected,
  type ReportFindingItem,
  type ReportSectionItem,
  type ReportSeverity,
} from "../report-data"

/** "Show": every finding, or one severity. */
export type SeverityFilter = "all" | ReportSeverity

/** "Sort by". */
export type ListSort = "priority" | "section" | "number" | "occurrence"

/** Highest stakes first, as the filter buttons read. */
const SEVERITY_ORDER: readonly ReportSeverity[] = ["needs_sme", "error", "warn"]

/** The filter's own wording: shorter than the row's, since it sits in a toolbar. */
export const SEVERITY_FILTER_LABEL: Record<ReportSeverity, string> = {
  needs_sme: "Specialist",
  error: "Must correct",
  warn: "Review",
}

/** The severities the report actually has, so the toolbar offers no empty filter. */
export function presentSeverities(counts: Record<ReportSeverity, number>): ReportSeverity[] {
  return SEVERITY_ORDER.filter((severity) => counts[severity] > 0)
}

function matches(filter: SeverityFilter, item: ReportFindingItem): boolean {
  return filter === "all" || item.severity === filter
}

/**
 * The flat list, filtered and ordered.
 *
 * "Priority" is the server's own order and is deliberately not re-sorted here:
 * the server ranks on the specialist's disposition as well as severity, and a
 * second, weaker copy of that rule would disagree with the PDF and CSV.
 */
export function arrangeFindings(
  items: readonly ReportFindingItem[],
  sort: Exclude<ListSort, "section">,
  filter: SeverityFilter,
): ReportFindingItem[] {
  const ordered =
    sort === "number"
      ? [...items].sort((a, b) => a.number - b.number)
      : sort === "occurrence"
        ? [...items].sort((a, b) => b.occurrenceCount - a.occurrenceCount || a.number - b.number)
        : [...items]
  return ordered.filter((item) => matches(filter, item))
}

export interface FindingGroup {
  key: string
  title: string
  findings: ReportFindingItem[]
}

/**
 * The list grouped by FIFRA section, in the server's section order and the
 * server's order within each. A section the filter empties is dropped rather
 * than left as a bare heading.
 */
export function groupBySection(
  items: readonly ReportFindingItem[],
  sections: readonly ReportSectionItem[],
  filter: SeverityFilter,
): FindingGroup[] {
  const byId = new Map<FindingId, ReportFindingItem>(items.map((item) => [item.id, item]))
  return sections
    .map((section, index) => ({
      key: section.sectionId ?? `unsectioned-${String(index)}`,
      title: section.title,
      findings: section.findingIds
        .map((id) => byId.get(id))
        .filter((item): item is ReportFindingItem => item !== undefined && matches(filter, item)),
    }))
    .filter((group) => group.findings.length > 0)
}

/**
 * Where a finding stands, in one word, for the row's meta line.
 *
 * First match wins: a correction outranks the client's response, which
 * outranks where the review team has it.
 */
export function findingStatus(item: ReportFindingItem): string {
  if (isCorrected(item)) return "Corrected"
  if (item.clientResolution === "disputed") return "Disputed"
  if (item.clientResolution === "done") return "Acknowledged"
  if (item.severity === "needs_sme") return "With a specialist"
  if (item.smeDecision === "confirm") return "Confirmed"
  return "Flagged"
}
